<template>
  <div class="data-table">
    <table>
      <thead v-if="headings.length > 0">
        <tr>
          <th
            v-for="head in headings"
            :key="head.key"
            :class="headingClasses(head)"
            @click="sort(head)"
          >
            <span>{{ head.label }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in displayRows"
          :key="row.id"
          :class="row.status"
          @click="navigate(row.number)"
        >
          <td
            v-for="head in headings"
            :key="head.key"
            :class="[head.key, head.keyClass ? row[head.key] : '']"
          >
            <slot :name="slotName(head)" :row="row" :head="head">
              <img v-if="head.type === 'image'" :src="row[head.key]" />
              <span v-else>{{ filter(row[head.key], head.filter) }}</span>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: "DataTable",
  props: {
    // Heading Row. Requires label and key properties
    headings: {
      type: Array,
      default: () => []
    },
    // Table Row Objects
    // Displayed value is based on the heading row key property.
    rows: {
      type: Array,
      default: () => []
    },
    // Optional propertly to disable sorting (set to false)
    sortable: {
      type: Boolean,
      default: true
    },
    // Optional property to enable lazy loading of data
    // Note, it's not real lazy loading...
    lazyLoad: {
      type: Boolean,
      default: false
    },
    // TODO Pagination
    perPage: {
      type: Number,
      default: 20
    },
    page: {
      type: Number,
      default: 1
    },
    searchQuery: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      sortedKey: null,
      sortDirection: "asc",
      sortType: "string",
    };
  },
  computed: {
    displayRows() {
      let rows = this.rows.slice(0);
    
      // Sort all rows
      if (this.sortable && this.sortedKey) {
        rows.sort((a, b) => {
          if (
            a.hasOwnProperty(this.sortedKey) &&
            b.hasOwnProperty(this.sortedKey) &&
            a[this.sortedKey] &&
            b[this.sortedKey]
          ) {
            if (this.sortType === "number") {
              if (this.sortDirection === "desc")
                return b[this.sortedKey] - a[this.sortedKey];
              else return a[this.sortedKey] - b[this.sortedKey];
            }
            if (this.sortDirection === "desc")
              return b[this.sortedKey].localeCompare(a[this.sortedKey]);
            else return a[this.sortedKey].localeCompare(b[this.sortedKey]);
          } else {
            return 0;
          }
        });
      }

      // Filter out rows
      if (this.searchQuery !== "") {
        const query = this.searchQuery.toLowerCase();
        rows = rows.filter(row => {
          for (const key in row) {
            if (
              row.hasOwnProperty(key) &&
              row[key] &&
              row[key]
                .toString()
                .toLowerCase()
                .indexOf(query) > -1
            )
              return true;
          }
          return false;
        });
      }

      if (this.perPage > 0) {
        
        const end = this.page * this.perPage;
        const start = this.lazyLoad ? 0 : end - this.perPage;
        rows = rows.slice(start, end);
      }
      return rows;
    },
    isAdmin(){
      return this.$store.getters.isAdmin;
    }
  },
  methods: {
    // Return the class names for each row
    headingClasses(head) {
      const classes = [];
      if (
        (this.sortable &&
          head.sortable !== undefined &&
          head.sortable === true) ||
        (this.sortable && head.sortable === undefined) ||
        head.sortable
      )
        classes.push("sortable");
      if (this.sortedKey === head.key)
        classes.push(`sort-${this.sortDirection}`);
      return classes;
    },
    sort(head) {
      if (this.sortedKey === head.key)
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      else if(head.sortable != false) {
        this.sortedKey = head.key;
        this.sortType = head.hasOwnProperty("sortType")
          ? head.sortType
          : "string";
        this.sortDirection = head.hasOwnProperty("sortReverse")
          ? "desc"
          : "asc";
      }
    },
    filter(value, filter) {
      if (value && filter && this.$options.filters[filter])
        return this.$options.filters[filter](value);
      else return value;
    },
    handleLazyLoad() {
      const bottomDistance =
        document.body.scrollHeight - window.innerHeight - window.scrollY;
      if (bottomDistance <= 100) {
        this.currentPage += 1;
      }
    },
    slotName(head) {
      return head.key + "Slot";
    },
    navigate(number) {
      if(number){
      this.$router.push({
        name: "orderDetails",
        params: { order: number }
      });
      }
    }
  },
  created() {
    let key = null;
    let direction = "asc";
    let type = "string";

    for (const head of this.headings) {
      if (head.hasOwnProperty("defaultSort")) {
        key = head.key;
        direction = head.hasOwnProperty("sortReverse") ? "desc" : "asc";
        type = head.hasOwnProperty("sortType")
          ? head.hasOwnProperty("sortType")
          : "string";
        break;
      }
    }
    if (key) {
      this.sortedKey = key;
      this.sortDirection = direction;
      this.sortType = type;
    }
    this.currentPage = this.page;

    if (this.lazyLoad) {
      window.addEventListener("scroll", this.handleLazyLoad);
    }
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleLazyLoad);
  }
};
</script>

<style lang="scss" scoped>

table {
  width: 100%;
}

th {
  text-align: left;
  white-space: nowrap;
  position: relative;

  &.sortable {
    cursor: pointer;

    &::after {
      content: "";
      border-style: solid;
      border-width: 0 4.5px 5px 4.5px;
      border-top-color: transparent;
      border-left-color: transparent;
      border-right-color: transparent;
      display: inline-block;
      margin-left: 10px;
      opacity: 0;
      width: 0;
      height: 0;
      vertical-align: middle;
      transition: all 200ms;
    }

    &.sort-asc::after,
    &.sort-desc::after {
      opacity: 1;
    }
    &.sort-desc::after {
      transform: rotate(180deg);
    }
  }
}

.admin {
  cursor: pointer;
}

.Approved {
  cursor: pointer;
  font-weight: 600;
}

.Revised {
  color: #C9C9C9;
  cursor: pointer;
  font-weight: 600;
}

.Pending{
  cursor: pointer;
}

.actions {
  width: 173px;
  padding: 10px 0;
}
</style>
